package app.bloomey.web.pages.admin

import androidx.compose.runtime.*
import app.bloomey.web.components.layouts.AdminPageLayout
import app.bloomey.web.components.widgets.*
import app.bloomey.web.core.data.UiState
import app.bloomey.web.modules.analytics.AnalyticsContract
import app.bloomey.web.modules.analytics.AnalyticsViewModel
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

@Page
@Composable
fun DashboardPage() {
    val scope = rememberCoroutineScope()
    val viewModel = remember { AnalyticsViewModel(scope) }
    val uiState by viewModel.observeStates().collectAsState()

    LaunchedEffect(Unit) {
        scope.launch {
            viewModel.send(AnalyticsContract.Inputs.GetTotalUsers)
            viewModel.send(AnalyticsContract.Inputs.MonthlyActiveUsers)
            viewModel.send(AnalyticsContract.Inputs.DailyActiveUsers)
            viewModel.send(AnalyticsContract.Inputs.NewUsers)
        }
    }

    AdminPageLayout(pageTitle = "Dashboard") {
        Height(30.px)
        SimpleGrid(
            numColumns = numColumns(base = 1, sm = 2),
            modifier = Modifier
                .fillMaxWidth()
                .gap(3.cssRem)
                .margin(bottom = 60.px)
        ) {
            StatsView(
                title = "Total Users",
                subtitle = "All Time Users",
                value = uiState.totalUsers.getDataOrNull?.lastOrNull()?.metaData?.toInt()
            ) {
                when (val state = uiState.totalUsers) {
                    is UiState.Error -> SiteErrorView(text = state.errorMessage) {
                        viewModel.trySend(AnalyticsContract.Inputs.GetTotalUsers)
                    }

                    UiState.Initial -> {}
                    UiState.Loading -> LoadingView()
                    is UiState.Success ->
                        ChartContainer(
                            data = state.data,
                            quantitative = {
                                it.metaData.toDouble()
                            }
                        ) {
                            it.timeStamp.date.month.name.take(3)
                        }
                }
            }
            StatsView(
                title = "Monthly Active Users",
                subtitle = "This Month",
                value = uiState.monthlyActiveUsers.getDataOrNull?.lastOrNull()?.metaData?.toInt()
            ) {
                when (val state = uiState.monthlyActiveUsers) {
                    is UiState.Error -> SiteErrorView(text = state.errorMessage) {
                        viewModel.trySend(AnalyticsContract.Inputs.MonthlyActiveUsers)
                    }

                    UiState.Initial -> {}
                    UiState.Loading -> LoadingView()
                    is UiState.Success ->
                        ChartContainer(
                            data = state.data,
                            quantitative = {
                                it.metaData.toDouble()
                            }
                        ) {
                            it.timeStamp.date.month.name.take(3)
                        }
                }
            }
            StatsView(
                title = "Daily Active Users",
                subtitle = "Today",
                value = uiState.dailyActiveUsers.getDataOrNull?.lastOrNull()?.metaData?.toInt()
            ) {
                when (val state = uiState.dailyActiveUsers) {
                    is UiState.Error -> SiteErrorView(text = state.errorMessage) {
                        viewModel.trySend(AnalyticsContract.Inputs.DailyActiveUsers)
                    }

                    UiState.Initial -> {}
                    UiState.Loading -> LoadingView()
                    is UiState.Success -> ChartContainer(
                        data = state.data,
                        quantitative = {
                            it.metaData.toDouble()
                        }
                    ) {
                        it.timeStamp.date.dayOfWeek.name.take(3)
                    }
                }
            }

            StatsView(
                title = "New Users",
                subtitle = "Today",
                value = uiState.newUsers.getDataOrNull?.lastOrNull()?.metaData?.toInt()
            ) {
                when (val state = uiState.newUsers) {
                    is UiState.Error -> SiteErrorView(text = state.errorMessage) {
                        viewModel.trySend(AnalyticsContract.Inputs.NewUsers)
                    }

                    UiState.Initial -> {}
                    UiState.Loading -> LoadingView()
                    is UiState.Success -> ChartContainer(
                        data = state.data,
                        quantitative = {
                            it.metaData.toDouble()
                        }
                    ) {
                        it.timeStamp.date.dayOfWeek.name.take(3)
                    }
                }
            }
        }
    }
}